import type { FC } from "react";
import { memo, useEffect } from "react";

import { DEPRECATED_Loading } from "@ui/components/DEPRECATED_Loading";
import { useFormContext } from "@ui/components/Form";
import { TransactionLinkWidgetEntryPoint } from "@ui/components/TransactionLinkWidgetEntryPoint";
import type { AsyncAvData } from "@ui/hooks/useSessionAsyncAv";
import { useTranslations } from "@ui/providers/useTranslations";
import classnames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { useAgeEstimationFallback } from "./hooks/useAgeEstimationFallback";
import { type AgeVerificationNavigation, useAgeVerificationFlow } from "./hooks/useAgeVerificationFlow";
import { useAvMethods } from "./hooks/useAvMethods";
import { useSyncWidgetButtonState } from "./hooks/useSyncWidgetButtonState/useSyncWidgetButtonState";
import type { AgeEstimationFlowConfig } from "./interfaces/AgeEstimationFlowConfig";
import type { AvFormValues } from "./interfaces/AvFormValues";
import type { AvUserDataType } from "./interfaces/AvUserDataType";
import { getAgeEstimationFallbackMethod, isAgeEstimationMethod } from "./utils/ageEstimationPolicy";

import { AgeEstimationFallbackDialog } from "./AgeEstimationFallbackDialog";
import { AgeVerificationContent } from "./AgeVerificationContent";

import styles from "./ValidationMethods.module.scss";

export type { AgeEstimationFlowConfig } from "./interfaces/AgeEstimationFlowConfig";

export type ValidationMethodsProps = {
  selectedMethod: string;
  isOptional?: boolean;
  showImage?: boolean;
  isPending?: boolean;
  onSuccess?: (avpUuid?: string) => void;
  onFail?: (message?: string) => void;
  selectMethod: (selectedMethod: string) => void;
  selectedMethodImage?: string;
  userData?: AvUserDataType;
  buttonClassName?: string;
  buttonLabel?: string;
  children?: React.ReactNode;
  isDisabled?: boolean;
  baseUrl?: string;
  onLoadDataFromCache?: (asyncAvData: AsyncAvData) => void;
  onSaveDataToCache?: () => void;
  ageEstimationFlow?: AgeEstimationFlowConfig;
  navigation: AgeVerificationNavigation;
};

export const ValidationMethods: FC<ValidationMethodsProps> = memo(
  ({
    selectedMethod,
    selectMethod,
    isOptional,
    isPending,
    selectedMethodImage,
    userData = {},
    showImage = true,
    onSuccess,
    onFail,
    buttonClassName,
    buttonLabel,
    children,
    isDisabled,
    baseUrl,
    onLoadDataFromCache,
    onSaveDataToCache,
    ageEstimationFlow,
    navigation,
  }) => {
    const { __ } = useTranslations();
    const form = useFormContext<AvFormValues>();

    const fallback = useAgeEstimationFallback();
    const isAgeEstimationFlowEnabled = Boolean(ageEstimationFlow);

    const { method, onSelectMethod, availableMethods, allAvailableMethods, isAvailableMethodsLoading } = useAvMethods({
      selectedMethod,
      selectMethod,
      isAgeEstimationFlowEnabled,
      authorizedAgeEstimationMethodCode: fallback.authorizedMethodCode,
    });

    useEffect(() => {
      dispatchTrackingEvent(TrackingEvents.TrackingHardAgeVerificationStart);
    }, []);

    const { isButtonDisabledForWidgetVerification, handleSyncAvWidgetVerification } = useSyncWidgetButtonState({
      selectedMethod,
      isOptional,
      form,
    });

    const {
      isAsyncPendingPopupOpened,
      hasAgeEstimationResponse,
      isValidationError,
      isVerifing,
      asyncVerificationPending,
      isAsyncVerificationFailed,
      isFallbackOpened,
      isFallbackInitPending,
      triggerVerification,
      tryAgainVerification,
      returnToAsyncVerificationProcess,
      cancelAsyncAgeVerificationProcess,
      continueWithFallback,
      cancelFallback,
    } = useAgeVerificationFlow({
      selectedMethod,
      selectMethod,
      availableMethods,
      allAvailableMethods,
      form,
      userData,
      isOptional,
      baseUrl,
      onSuccess,
      onFail,
      onLoadDataFromCache,
      onSaveDataToCache,
      isAgeEstimationFlowEnabled,
      isAvailableMethodsLoading,
      ageEstimationFlow,
      fallback,
      navigation,
    });

    const isAgeEstimationActive =
      isAgeEstimationFlowEnabled && availableMethods.length === 1 && isAgeEstimationMethod(availableMethods[0].code);
    const canContinueWithIdVerification =
      isAsyncPendingPopupOpened &&
      !isValidationError &&
      !isAsyncVerificationFailed &&
      isAgeEstimationActive &&
      Boolean(getAgeEstimationFallbackMethod(allAvailableMethods));

    if (isAvailableMethodsLoading) {
      return <DEPRECATED_Loading dataTestId="ageVerificationAvailableMethodsLoader" />;
    }

    return (
      <>
        {availableMethods?.length ? (
          <div data-testid="ageVerificationMethods" className={styles.formContainer}>
            <AgeVerificationContent
              availableMethods={availableMethods}
              selectedMethod={selectedMethod}
              showImage={showImage && !isAgeEstimationActive}
              selectedMethodImage={selectedMethodImage}
              method={method}
              isOptional={isOptional}
              isAsyncPendingPopupOpened={isAsyncPendingPopupOpened}
              isValidationError={isValidationError}
              isAsyncVerificationFailed={isAsyncVerificationFailed}
              onSelectMethod={onSelectMethod}
              handleSyncAvWidgetVerification={handleSyncAvWidgetVerification}
              cancelAsyncAgeVerificationProcess={cancelAsyncAgeVerificationProcess}
              returnToAsyncVerificationProcess={returnToAsyncVerificationProcess}
              tryAgainVerification={tryAgainVerification}
              onContinueWithIdVerification={canContinueWithIdVerification ? continueWithFallback : undefined}
              isContinueWithIdVerificationDisabled={!hasAgeEstimationResponse}
            >
              {children}
            </AgeVerificationContent>
            <AgeEstimationFallbackDialog
              isOpened={isFallbackOpened}
              isPending={isFallbackInitPending}
              onClose={cancelFallback}
              onContinue={continueWithFallback}
            />
            <Button
              pending={isPending || isVerifing || asyncVerificationPending}
              disabled={isDisabled || isButtonDisabledForWidgetVerification}
              variant="primary"
              data-testid="ageVerificationSubmitButton"
              onClick={triggerVerification}
              className={classnames(styles.submitButton, buttonClassName)}
            >
              {buttonLabel || __("profile.age_verification.submit")}
            </Button>
          </div>
        ) : (
          <p>{__("auth.age_verification.no_services_message")}</p>
        )}
        <TransactionLinkWidgetEntryPoint />
      </>
    );
  }
);
